import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom-v5-compat'
import { isUndefined } from 'lodash'
import { settingsPath } from 'lib/urls'
import * as BLConstants from 'shared/constants'
import sha1 from 'crypto-js/sha1'
import { sentiments } from 'registry/constants'
import { setCurrentUser } from 'shared/utils/setCurrentUser/setCurrentUser'
import { signup } from '../../../actions'
import {
  getSentiment,
  getUserEmail,
  getUserFullName,
  getUserId,
} from '../../../reducers'
import Slide, { Title } from '../../Slide/Slide'
import SignupForm, { SignupFunction } from '../../signup-form/signup-form'
import { NextButton } from '../../OnboardingButtons/OnboardingButtons'
import css from './Signup.styles.scss'
import { getQueryParam } from 'utils'
import { SignupSuccessResponse } from '../../signup-form/signup-form'
import { bindActionCreators } from 'redux'

const trackRegistryCreate = ({ registry, user }: SignupSuccessResponse) => {
  if (!isUndefined(window.ire)) {
    window.ire(
      'trackConversion',
      25150,
      {
        orderId: registry.id,
        customerId: registry.id,
        customerEmail: sha1(user.email).toString(),
      },
      {
        verifySiteDefinitionMatch: true,
      }
    )
  }

  if (!isUndefined(window.ga)) {
    window.ga('send', {
      hitType: 'event', // required
      eventCategory: 'Registry', // required
      eventAction: 'create', // required
      eventValue: 0, // required
    })
  }

  // Remove the existing currentUser script tag (_js_current_user.html.erb)
  // and inject a new one so that it can be picked up by GoogleTagManager tracking.
  $('script#currentUser').remove()
  $('body').append(
    `<script id="currentUser">window.currentUser = ${JSON.stringify(
      user
    )}</script>`
  )
  setCurrentUser(user)

  // Facebook tracking
  window.fbq(
    'trackCustom',
    'RegistryCreated',
    {},
    { eventID: `registryCreated:${registry?.id}` }
  )

  // Yahoo tracking
  window.dotq.push({
    projectId: BLConstants.YAHOO_DOT_TAG_PROJECT_ID,
    properties: {
      pixelId: BLConstants.YAHOO_DOT_TAG_PIXEL_ID,
      qstrings: {
        et: 'custom',
        ec: 'registry',
        ea: 'create',
      },
    },
  })
}

interface SignupProps {
  nextPath: string
  ssoEnabled: boolean
}

const Signup = ({ nextPath, ssoEnabled }: SignupProps) => {
  const { email, name, sentiment, signedUp } = useSelector((state: any) => ({
    email: getUserEmail(state),
    name: getUserFullName(state),
    sentiment: getSentiment(state),
    signedUp: !!getUserId(state),
  }))
  const navigate = useNavigate()

  // Use alreadySignedUp state instead of using signedUp directly so that
  // we do not immediately transition to the disabled signup form after signup.
  // Instead, only show this state when going back or opening for the first time.
  const [alreadySignedUp] = useState<boolean>(signedUp)

  const intialTitle = () => {
    switch (sentiment) {
      case sentiments.OVERWHELMED:
        return 'Create your registry and we will help you every step of the way.'
      case sentiments.EXPLORING:
        return `It's free and takes less than a minute to start building your registry.`
      default:
        return `Let's create your registry together.`
    }
  }

  const initialSubTitle = () => {
    switch (sentiment) {
      case sentiments.OVERWHELMED:
        return `Don't worry. You got this!`
      case sentiments.EXPLORING:
        return `No pressure!`
      default:
        return `We're excited too!`
    }
  }

  const [subTitle, setSubTitle] = useState(initialSubTitle)
  const [title, setTitle] = useState(intialTitle)
  const [subSubTitle, setSubSubTitle] = useState(false)

  const dispatch = useDispatch()
  // TypeScript is having trouble inferring the correct type of the result of bindActionCreators
  // likely due to our version of redux not being up-to-date
  // @ts-ignore
  const boundSignup: SignupFunction = bindActionCreators(signup, dispatch)

  const goToNext = () => navigate(nextPath)

  const onSubmitSuccess = (result: SignupSuccessResponse) => {
    trackRegistryCreate(result)
    goToNext()
  }

  const setHeaderState = ({
    title,
    subTitle,
    subSubTitle,
  }: {
    title: string
    subTitle: string
    subSubTitle: boolean
  }) => {
    setTitle(title)
    setSubTitle(subTitle)
    setSubSubTitle(subSubTitle)
  }

  const password = alreadySignedUp ? '****************' : undefined

  const lpContent = getQueryParam('lp_content')

  if (lpContent == 'checklist') {
    return (
      <Slide>
        <div className={css.textContainer}>
          <p className={css.subTitle}>{subTitle}</p>
          <Title className={css.title}>
            Create an account to save your checklist progress
          </Title>
        </div>
        <SignupForm
          initialValues={{ name, email, password }}
          setHeaderState={setHeaderState}
          signedUp={alreadySignedUp}
          submitButtonText="Create My Checklist"
          onSubmit={boundSignup}
          onSubmitSuccess={onSubmitSuccess}
          ssoEnabled={ssoEnabled}
        />
        {alreadySignedUp ? (
          <div>
            <p className="text-center small mbl">
              You're signed up!
              <br />
              You can update your name, email and password in{' '}
              <a href={settingsPath}>Settings</a>.
            </p>
            <NextButton to={nextPath} />
          </div>
        ) : null}
      </Slide>
    )
  }

  return (
    <Slide>
      <div className={css.textContainer}>
        {!subSubTitle && <p className={css.subTitle}>{subTitle}</p>}
        <Title className={css.title}>{title}</Title>
        {subSubTitle && <p className={css.subTitle}>{subTitle}</p>}
      </div>
      <SignupForm
        initialValues={{ name, email, password }}
        setHeaderState={setHeaderState}
        signedUp={alreadySignedUp}
        onSubmit={boundSignup}
        onSubmitSuccess={onSubmitSuccess}
        ssoEnabled={ssoEnabled}
      />
      {signedUp ? (
        <div>
          <p className="text-center small mbl">
            You're signed up!
            <br />
            You can update your name, email and password in{' '}
            <a href={settingsPath}>Settings</a>.
          </p>
          <NextButton to={nextPath} />
        </div>
      ) : null}
    </Slide>
  )
}

export default Signup
